import { useAuth } from '#globalContexts/AuthProvider';
import { useUserSubscription } from '#settingsFeature/models/billing/queries';

import { resolveSubscriptionView } from '../utils/subscriptionState';

// A backend that does not send `retention` yet must degrade to "no offers", never to a crash.
const NO_RETENTION = {
	canCancel: false,
	canRevertCancel: false,
	canPause: false,
	pauseBlockedReason: null,
	canResume: false,
	pauseOptions: [],
};

/**
 * Derives payment-health and lifecycle flags from the org's subscription. Generic — used by the
 * global banners and the billing settings; `isPaused` follows the org plan (the access signal) while
 * `view` decides which billing UI to render.
 *
 * @returns {{
 *   status: (string|null),
 *   isPastDue: boolean,
 *   isUnpaid: boolean,
 *   needsPaymentUpdate: boolean,
 *   isLoading: boolean,
 *   view: ('none'|'active'|'cancelling'|'paused'|'pauseScheduled'),
 *   isPaused: boolean,
 *   isCancelling: boolean,
 *   currentPeriodEnd: (string|null),
 *   pause: (null|{ startsAt: string, resumesAt: (string|null) }),
 *   retention: { canCancel: boolean, canRevertCancel: boolean, canPause: boolean, pauseBlockedReason: (string|null), canResume: boolean, pauseOptions: Array<{ months: number, startsAt: string, resumesAt: string }> },
 *   isOwner: boolean,
 * }}
 */
export function useBillingStatus() {
	const { data: subscription, isLoading } = useUserSubscription();
	const { orgPlan, orgRole } = useAuth();
	const status = subscription?.subscriptionStatus ?? null;

	const isPastDue = status === 'past_due';
	const isUnpaid = status === 'unpaid' || status === 'incomplete';
	const needsPaymentUpdate = isPastDue || isUnpaid;

	return {
		status,
		isPastDue,
		isUnpaid,
		needsPaymentUpdate,
		isLoading,
		view: resolveSubscriptionView({ subscription, orgPlan }),
		isPaused: orgPlan === 'paused',
		isCancelling: Boolean(subscription?.cancelAtPeriodEnd),
		currentPeriodEnd: subscription?.currentPeriodEnd ?? null,
		pause: subscription?.pause ?? null,
		retention: subscription?.retention ?? NO_RETENTION,
		isOwner: orgRole === 'owner',
	};
}
